<template>
    <main class="recorder-shell">
        <section class="recorder-panel">
            <header class="recorder-header" v-if="false">
                <div>
                    <template v-if="false">
                        <p class="eyebrow">Klingo Assistente</p>
                        <h1>Transcricao de audio</h1>
                    </template>
                </div>

                <div class="status-pill" :class="statusClass">
                    <span class="status-dot"></span>
                    <span>{{ statusLabel }}</span>
                </div>
            </header>

            <div class="recorder-grid">
                <section class="control-surface" aria-label="Controles de gravacao">

                    <div class="status-pill" :class="statusClass">
                        <span class="status-dot"></span>
                        <span>{{ statusLabel }}</span>
                    </div>

                    <label class="input-select-field" for="audio-input">
                        <span>Microfone</span>
                        <select
                            id="audio-input"
                            v-model="selectedAudioInputId"
                            :disabled="isAudioInputSelectDisabled"
                            @focus="loadAudioInputDevices"
                        >
                            <option value="">Padrao do navegador</option>
                            <option
                                v-for="device in audioInputDevices"
                                :key="device.deviceId"
                                :value="device.deviceId"
                            >
                                {{ device.label }}
                            </option>
                        </select>
                    </label>

                    <button
                        v-if="!isRecording"
                        class="record-button"
                        type="button"
                        :disabled="isBusy"
                        @click="startRecording"
                    >
                        <span class="record-icon"></span>
                        <span>Gravar</span>
                    </button>

                    <button
                        v-else
                        class="stop-button"
                        type="button"
                        :disabled="isStopping"
                        @click="stopRecording"
                    >
                        <span class="stop-icon"></span>
                        <span>Parar</span>
                    </button>

                    <div
                        class="audio-meter"
                        :class="{ 'is-active': isRecording, 'has-signal': audioLevel > 0.08 }"
                        aria-label="Nivel de entrada de audio"
                    >
                        <div class="audio-meter-title">
                            <span>Entrada de audio</span>
                            <strong>{{ audioMeterLabel }}</strong>
                        </div>

                        <div class="audio-meter-bars" aria-hidden="true">
                            <span
                                v-for="bar in audioMeterBars"
                                :key="bar.index"
                                class="audio-meter-bar"
                                :style="bar.style"
                            ></span>
                        </div>
                    </div>

                    <dl class="metrics">
                        <div>
                            <dt>Tempo</dt>
                            <dd>{{ elapsedLabel }}</dd>
                        </div>
                        <div>
                            <dt>Chunks</dt>
                            <dd>{{ chunksLabel }}</dd>
                        </div>
                        <div>
                            <dt>Janela</dt>
                            <dd>{{ chunkWindowLabel }}</dd>
                        </div>
                    </dl>

                    <dl class="processing-metrics">
                        <div>
                            <dt>Transcricao apos parar</dt>
                            <dd>{{ transcriptionProcessingLabel }}</dd>
                        </div>
                        <div>
                            <dt>SOAP + JSON</dt>
                            <dd>
                                <strong>{{ soapProcessingLabel }}</strong>
                                <small v-if="soapTimingDetail">{{ soapTimingDetail }}</small>
                            </dd>
                        </div>
                    </dl>

                    <div v-if="sessionId" class="session-box">
                        <span>Sessao</span>
                        <code>{{ sessionId }}</code>
                    </div>
                </section>

                <div class="document-column">
                    <section class="transcript-surface" aria-label="Transcricao">
                        <div class="transcript-toolbar">
                            <h2>Transcricao</h2>
                            <span>{{ progressLabel }}</span>
                        </div>

                        <textarea
                            class="transcript-area"
                            :value="transcriptText"
                            readonly
                            placeholder="A transcricao aparecera aqui."
                        ></textarea>

                        <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
                    </section>

                    <section class="history-surface" aria-label="Historico previo">
                        <div class="transcript-toolbar">
                            <h2>Historico previo</h2>
                            <span>JSON opcional</span>
                        </div>

                        <textarea
                            class="history-area"
                            v-model="patientHistoryText"
                            placeholder='Ex.: {"consultas":[{"data":"2026-06-01","queixa":"dor lombar","conduta":"fisioterapia"}]}'
                        ></textarea>

                        <p v-if="patientHistoryErrorMessage" class="error-message">{{ patientHistoryErrorMessage }}</p>
                    </section>

                    <section class="soap-surface" aria-label="Estruturacao SOAP">
                        <div class="transcript-toolbar">
                            <h2>SOAP</h2>
                            <span>{{ soapStatusLabel }}</span>
                        </div>

                        <div class="soap-controls">
                            <label for="soap-prompt">Prompt adicional</label>
                            <textarea
                                id="soap-prompt"
                                class="prompt-area"
                                v-model="soapPrompt"
                                :disabled="isSoapBusy"
                                placeholder="Ex.: destacar orientacoes ao paciente e plano de retorno."
                            ></textarea>

                            <button
                                class="soap-button"
                                type="button"
                                :disabled="!canGenerateSoap"
                                @click="generateSoapNote"
                            >
                                {{ soapButtonLabel }}
                            </button>
                        </div>

                        <textarea
                            class="soap-area"
                            :value="soapNote"
                            readonly
                            placeholder="A nota SOAP aparecera aqui."
                        ></textarea>

                        <div class="json-toolbar">
                            <h3>Analise do historico</h3>
                        </div>

                        <textarea
                            class="history-analysis-area"
                            :value="historyAnalysisText"
                            readonly
                            placeholder="Quando um historico previo for informado, a analise comparativa aparecera aqui."
                        ></textarea>

                        <div class="json-toolbar">
                            <h3>JSON estruturado</h3>
                        </div>

                        <textarea
                            class="json-area"
                            :value="soapJsonText"
                            readonly
                            placeholder="O JSON SOAP com CIDs candidatos, receita e solicitacoes aparecera aqui."
                        ></textarea>

                        <p v-if="soapErrorMessage" class="error-message">{{ soapErrorMessage }}</p>
                    </section>
                </div>
            </div>

            <section class="chat-surface" aria-label="Perguntas sobre historico e consulta">
                <div class="cost-summary-header">
                    <h2>Chat clinico</h2>
                    <span>{{ chatStatusLabel }}</span>
                </div>

                <div class="chat-messages">
                    <div
                        v-for="message in chatMessages"
                        :key="message.id"
                        class="chat-message"
                        :class="`is-${message.role}`"
                    >
                        <strong>{{ message.role === 'user' ? 'Pergunta' : 'Resposta' }}</strong>
                        <p>{{ message.text }}</p>
                    </div>

                    <p v-if="chatMessages.length === 0" class="chat-empty">
                        As respostas consideram o historico JSON informado, a transcricao e o SOAP/JSON ja gerado.
                    </p>
                </div>

                <div class="chat-controls">
                    <textarea
                        v-model="chatQuestion"
                        :disabled="isChatBusy"
                        placeholder="Pergunte sobre evolucao, recorrencias, divergencias ou pontos relevantes da consulta atual."
                    ></textarea>

                    <button
                        type="button"
                        :disabled="!canAskChat"
                        @click="askChatQuestion"
                    >
                        {{ chatButtonLabel }}
                    </button>
                </div>

                <p v-if="chatErrorMessage" class="error-message">{{ chatErrorMessage }}</p>
            </section>

            <section class="cost-summary-surface" aria-label="Resumo de custos OpenAI">
                <div class="cost-summary-header">
                    <h2>Custos OpenAI</h2>
                    <strong>{{ totalCostLabel }}</strong>
                </div>

                <div class="cost-grid">
                    <div class="cost-item">
                        <span>Transcricao</span>
                        <strong>{{ transcriptionCostLabel }}</strong>
                        <small>{{ transcriptionCostDetail }}</small>
                    </div>

                    <div class="cost-item">
                        <span>SOAP + JSON</span>
                        <strong>{{ soapCostLabel }}</strong>
                        <small>{{ soapCostDetail }}</small>
                    </div>
                </div>
            </section>
        </section>
    </main>
</template>

<script>
import defaultAxios from 'axios';

export default {
    name: 'AudioTranscriptionRecorder',

    props: {
        apiBaseUrl: {
            type: String,
            default: function () {
                return typeof window !== 'undefined' && window.KLINGO_ASSISTENTE_API_BASE_URL
                    ? String(window.KLINGO_ASSISTENTE_API_BASE_URL)
                    : '';
            },
        },
        initialRecordingToken: {
            type: String,
            default: function () {
                return typeof window !== 'undefined' && window.KLINGO_RECORDING_TOKEN
                    ? String(window.KLINGO_RECORDING_TOKEN)
                    : '';
            },
        },
        initialPatientHistoryJson: {
            type: [Array, Object, String],
            default: null,
        },
        initialSoapPrompt: {
            type: String,
            default: '',
        },
        pusherConfig: {
            type: Object,
            default: null,
        },
        axiosClient: {
            type: [Object, Function],
            default: null,
        },
        requestHeaders: {
            type: Object,
            default: function () {
                return {};
            },
        },
        withCredentials: {
            type: Boolean,
            default: false,
        },
        autoGenerateSoap: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            sessionId: null,
            recordingToken: '',
            sessionClientToken: '',
            stream: null,
            audioContext: null,
            sourceNode: null,
            processorNode: null,
            wavBuffers: [],
            wavSampleCount: 0,
            wavSampleRate: 0,
            audioLevel: 0,
            chunkTimer: null,
            chunkIndex: 0,
            chunkDuration: 10000,
            acceptedMimeTypes: [],
            directUpload: {
                enabled: false,
                disk: null,
                expiresInSeconds: 0,
            },
            isRecording: false,
            isStopping: false,
            pendingUploads: 0,
            status: 'idle',
            partialText: '',
            finalText: '',
            chunksDone: 0,
            chunksTotal: 0,
            soapStatus: 'idle',
            soapNote: '',
            soapJson: null,
            soapPrompt: '',
            soapErrorMessage: '',
            patientHistoryText: '',
            patientHistoryErrorMessage: '',
            chatQuestion: '',
            chatMessages: [],
            chatMessageIndex: 0,
            isChatBusy: false,
            chatErrorMessage: '',
            errorMessage: '',
            costs: null,
            timings: null,
            audioInputDevices: [],
            selectedAudioInputId: '',
            pollingTimer: null,
            pusherClient: null,
            pusherChannel: null,
            pusherChannelName: null,
            realtimeRefreshTimer: null,
            elapsedTimer: null,
            elapsedSeconds: 0,
            processingTimer: null,
            processingNow: Date.now(),
            transcriptionProcessingStartedAt: null,
            transcriptionProcessingSeconds: null,
            transcriptionProcessingFailed: false,
            soapProcessingStartedAt: null,
            soapProcessingSeconds: null,
            soapProcessingFailed: false,
            autoSoapRequested: false,
            completedEventKey: '',
        };
    },

    computed: {
        isBusy() {
            return this.status === 'starting' || this.isStopping;
        },

        isAudioInputSelectDisabled() {
            return this.status === 'starting' || this.isRecording || this.isStopping;
        },

        audioMeterLabel() {
            if (this.isStopping) {
                return 'Finalizando';
            }

            if (!this.isRecording) {
                return this.status === 'starting' ? 'Inicializando' : 'Em espera';
            }

            if (this.audioLevel >= 0.58) {
                return 'Sinal forte';
            }

            if (this.audioLevel >= 0.16) {
                return 'Detectando';
            }

            return 'Sinal baixo';
        },

        audioMeterBars() {
            const level = this.isRecording ? this.audioLevel : 0;
            const shape = [0.22, 0.42, 0.68, 0.88, 0.58, 0.82, 1, 0.74, 0.92, 0.64, 0.78, 0.5, 0.34, 0.24];
            const bars = [];

            for (let index = 0; index < shape.length; index += 1) {
                const idleScale = 0.12 + (shape[index] * 0.08);
                const activeScale = Math.min(1, idleScale + (level * shape[index] * 0.88));
                const scale = this.isRecording ? Math.max(idleScale, activeScale) : idleScale;
                const opacity = this.isRecording
                    ? Math.min(1, 0.42 + (level * 0.5) + (shape[index] * 0.08))
                    : 0.35;

                bars.push({
                    index,
                    style: {
                        transform: `scaleY(${scale.toFixed(2)})`,
                        opacity: opacity.toFixed(2),
                    },
                });
            }

            return bars;
        },

        displayStatus() {
            if (
                this.status === 'processing'
                && this.pendingUploads === 0
                && this.isTranscriptionReadyForDisplay()
            ) {
                return 'transcription_ready';
            }

            return this.status;
        },

        statusClass() {
            const status = this.displayStatus;

            return {
                'is-recording': this.isRecording,
                'is-processing': status === 'processing',
                'is-done': status === 'done' || status === 'transcription_ready',
                'is-error': status === 'error',
            };
        },

        statusLabel() {
            const labels = {
                idle: 'Pronto',
                starting: 'Iniciando',
                recording: 'Gravando',
                processing: 'Processando',
                transcription_ready: 'Transcricao pronta',
                done: 'Concluido',
                error: 'Erro',
            };

            return labels[this.displayStatus] || this.displayStatus;
        },

        elapsedLabel() {
            const minutes = String(Math.floor(this.elapsedSeconds / 60)).padStart(2, '0');
            const seconds = String(this.elapsedSeconds % 60).padStart(2, '0');

            return `${minutes}:${seconds}`;
        },

        chunksLabel() {
            if (!this.chunksTotal) {
                return this.pendingUploads ? `${this.pendingUploads} enviando` : '0';
            }

            return `${this.chunksDone}/${this.chunksTotal}`;
        },

        chunkWindowLabel() {
            return `${Math.round(this.chunkDuration / 1000)}s`;
        },

        transcriptionProcessingLabel() {
            if (this.transcriptionProcessingFailed) {
                return 'Erro';
            }

            return this.processingLabel(
                this.transcriptionProcessingSeconds,
                this.transcriptionProcessingStartedAt,
                'Aguardando parada'
            );
        },

        soapProcessingLabel() {
            if (this.soapProcessingFailed) {
                return 'Erro';
            }

            return this.processingLabel(
                this.soapProcessingSeconds,
                this.soapProcessingStartedAt,
                'Aguardando SOAP'
            );
        },

        soapTimingDetail() {
            const soap = this.timings && this.timings.soap ? this.timings.soap : null;

            if (!soap) {
                return '';
            }

            const parts = [];

            if (this.hasTimingValue(soap.queue_wait_seconds)) {
                parts.push(`fila ${this.formatDuration(soap.queue_wait_seconds)}`);
            }

            if (this.hasTimingValue(soap.openai_seconds)) {
                parts.push(`OpenAI ${this.formatDuration(soap.openai_seconds)}`);
            }

            if (this.hasTimingValue(soap.job_seconds)) {
                parts.push(`job ${this.formatDuration(soap.job_seconds)}`);
            }

            if (this.hasTimingValue(soap.total_seconds)) {
                parts.push(`total ${this.formatDuration(soap.total_seconds)}`);
            }

            return parts.join(' | ');
        },

        progressLabel() {
            if (!this.chunksTotal) {
                return 'Aguardando audio';
            }

            return `${this.chunksDone} de ${this.chunksTotal} chunks`;
        },

        transcriptText() {
            return this.finalText || this.partialText;
        },

        isSoapBusy() {
            return this.soapStatus === 'pending' || this.soapStatus === 'processing';
        },

        isTranscriptionReadyForSoap() {
            return this.pendingUploads === 0 && this.isTranscriptionReadyForDisplay();
        },

        canGenerateSoap() {
            return !!this.sessionId
                && !this.isRecording
                && !this.isSoapBusy
                && this.isTranscriptionReadyForSoap;
        },

        canAskChat() {
            return !!this.sessionId
                && !this.isRecording
                && !this.isChatBusy
                && this.chatQuestion.trim().length > 0
                && (this.transcriptText.trim().length > 0 || this.patientHistoryText.trim().length > 0);
        },

        soapStatusLabel() {
            const labels = {
                idle: 'Aguardando',
                pending: 'Na fila',
                processing: 'Gerando',
                done: 'Concluido',
                error: 'Erro',
            };

            return labels[this.soapStatus] || this.soapStatus;
        },

        soapButtonLabel() {
            if (this.soapStatus === 'pending') {
                return 'Na fila';
            }

            if (this.soapStatus === 'processing') {
                return 'Gerando';
            }

            if (!!this.sessionId && !this.isTranscriptionReadyForSoap) {
                return 'Aguardando transcricao';
            }

            return this.soapNote ? 'Gerar novamente' : 'Gerar SOAP';
        },

        soapJsonText() {
            if (!this.soapJson) {
                return '';
            }

            return JSON.stringify(this.soapJson, null, 2);
        },

        historyAnalysisText() {
            const analysis = this.soapJson && this.soapJson.analise_historico
                ? this.soapJson.analise_historico
                : null;

            if (!analysis) {
                return '';
            }

            return Object.keys(analysis).map((key) => {
                return `${this.humanizeKey(key)}: ${this.formatAnalysisValue(analysis[key])}`;
            }).join('\n');
        },

        chatStatusLabel() {
            return this.isChatBusy ? 'Respondendo' : `${this.chatMessages.length} mensagens`;
        },

        chatButtonLabel() {
            return this.isChatBusy ? 'Enviando' : 'Perguntar';
        },

        transcriptionCostLabel() {
            return this.formatCost(this.costNumber('transcription.cost_usd'));
        },

        soapCostLabel() {
            return this.formatCost(this.costNumber('soap.cost_usd'));
        },

        totalCostLabel() {
            return this.formatCost(this.costNumber('total.cost_usd'));
        },

        transcriptionCostDetail() {
            const seconds = this.costNumber('transcription.duration_seconds');
            const model = this.costString('transcription.model');
            const duration = seconds > 0 ? this.formatDuration(seconds) : 'sem audio processado';

            return `${duration} | ${model}`;
        },

        soapCostDetail() {
            const inputTokens = this.costNumber('soap.input_tokens');
            const outputTokens = this.costNumber('soap.output_tokens');
            const model = this.costString('soap.model');

            if (!inputTokens && !outputTokens) {
                return `sem tokens registrados | ${model}`;
            }

            return `${inputTokens} entrada / ${outputTokens} saida | ${model}`;
        },
    },

    mounted() {
        this.recordingToken = this.resolveInitialRecordingToken();
        this.applyInitialClinicalContext();
        this.loadAudioInputDevices();

        if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
            navigator.mediaDevices.addEventListener('devicechange', this.loadAudioInputDevices);
        }
    },

    beforeDestroy() {
        if (navigator.mediaDevices && navigator.mediaDevices.removeEventListener) {
            navigator.mediaDevices.removeEventListener('devicechange', this.loadAudioInputDevices);
        }

        this.cleanup();
    },

    methods: {
        async startRecording() {
            this.errorMessage = '';
            this.status = 'starting';
            this.unsubscribeFromRealtimeSession();

            try {
                if (!navigator.mediaDevices || !this.resolveAudioContextConstructor()) {
                    throw new Error('Este navegador nao oferece suporte a gravacao de audio.');
                }

                const startPayload = {};

                if (this.recordingToken) {
                    startPayload.recording_token = this.recordingToken;
                }

                const { data } = await this.httpClient().post(
                    this.apiUrl('/api/audio-transcriptions/start'),
                    startPayload,
                    this.authorizedAxiosConfig()
                );

                this.sessionId = data.session_id;
                this.sessionClientToken = data.session_client_token || '';
                this.chunkDuration = data.chunk_duration;
                this.acceptedMimeTypes = data.accepted_mime_types || [];
                this.directUpload = this.normalizeDirectUploadConfig(data.direct_upload);
                this.chunkIndex = 0;
                this.partialText = '';
                this.finalText = '';
                this.chunksDone = 0;
                this.chunksTotal = 0;
                this.soapStatus = 'idle';
                this.soapNote = '';
                this.soapJson = null;
                this.soapPrompt = this.initialSoapPrompt ? String(this.initialSoapPrompt) : '';
                this.soapErrorMessage = '';
                this.patientHistoryErrorMessage = '';
                this.chatQuestion = '';
                this.chatMessages = [];
                this.chatMessageIndex = 0;
                this.chatErrorMessage = '';
                this.costs = null;
                this.timings = null;
                if (data.patient_history_json && !this.patientHistoryText.trim()) {
                    this.patientHistoryText = JSON.stringify(data.patient_history_json, null, 2);
                }
                this.soapPrompt = data.soap_prompt || this.soapPrompt || '';
                this.elapsedSeconds = 0;
                this.transcriptionProcessingStartedAt = null;
                this.transcriptionProcessingSeconds = null;
                this.transcriptionProcessingFailed = false;
                this.soapProcessingStartedAt = null;
                this.soapProcessingSeconds = null;
                this.soapProcessingFailed = false;
                this.autoSoapRequested = false;
                this.completedEventKey = '';
                this.resetAudioMeter();
                this.stopProcessingTimerIfIdle();
                this.startStatusUpdates();
                this.$emit('session-started', data);

                this.stream = await navigator.mediaDevices.getUserMedia(this.audioInputConstraints());
                this.loadAudioInputDevices();
                this.isRecording = true;
                this.startWaveRecorder();
                this.status = 'recording';
                this.startElapsedTimer();
            } catch (error) {
                this.isRecording = false;
                this.status = 'error';
                this.errorMessage = this.extractErrorMessage(error);
                this.$emit('recording-error', {
                    message: this.errorMessage,
                    error,
                });
                this.unsubscribeFromRealtimeSession();
                this.stopAudioGraph();
                this.stopStream();
            }
        },

        async loadAudioInputDevices() {
            if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
                this.audioInputDevices = [];
                return;
            }

            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const audioInputs = devices.filter((device) => {
                    return device.kind === 'audioinput' && device.deviceId;
                });

                this.audioInputDevices = audioInputs.map((device, index) => {
                    return {
                        deviceId: device.deviceId,
                        label: device.label || `Microfone ${index + 1}`,
                    };
                });

                const selectedDeviceExists = this.audioInputDevices.some((device) => {
                    return device.deviceId === this.selectedAudioInputId;
                });

                if (this.selectedAudioInputId && !selectedDeviceExists) {
                    this.selectedAudioInputId = '';
                }
            } catch (error) {
                this.audioInputDevices = [];
            }
        },

        audioInputConstraints() {
            if (!this.selectedAudioInputId) {
                return { audio: true };
            }

            return {
                audio: {
                    deviceId: { exact: this.selectedAudioInputId },
                },
            };
        },

        async stopRecording() {
            if (!this.stream || this.isStopping) {
                return;
            }

            this.isStopping = true;
            this.errorMessage = '';
            this.transcriptionProcessingStartedAt = Date.now();
            this.transcriptionProcessingSeconds = null;
            this.transcriptionProcessingFailed = false;
            this.startProcessingTimer();

            try {
                await this.stopRecorder();
                this.stopStream();
                await this.waitForPendingUploads();

                const { data } = await this.httpClient().post(
                    this.apiUrl('/api/audio-transcriptions/finish'),
                    {
                        session_id: this.sessionId,
                    },
                    this.authorizedAxiosConfig()
                );

                this.applyStatusPayload(data);
                this.startStatusUpdates();
            } catch (error) {
                this.status = 'error';
                this.errorMessage = this.extractErrorMessage(error);
                this.transcriptionProcessingStartedAt = null;
                this.transcriptionProcessingFailed = true;
                this.$emit('recording-error', {
                    message: this.errorMessage,
                    error,
                });
                this.stopProcessingTimerIfIdle();
            } finally {
                this.isRecording = false;
                this.isStopping = false;
                this.stopElapsedTimer();
            }
        },

        startWaveRecorder() {
            const AudioContextConstructor = this.resolveAudioContextConstructor();

            this.audioContext = new AudioContextConstructor();
            this.wavSampleRate = this.audioContext.sampleRate;
            this.wavBuffers = [];
            this.wavSampleCount = 0;

            this.sourceNode = this.audioContext.createMediaStreamSource(this.stream);
            this.processorNode = this.audioContext.createScriptProcessor(4096, 1, 1);

            this.processorNode.onaudioprocess = (event) => {
                if (!this.isRecording || this.isStopping) {
                    return;
                }

                const input = event.inputBuffer.getChannelData(0);
                this.updateAudioMeter(input);

                const samples = new Float32Array(input.length);
                samples.set(input);

                this.wavBuffers.push(samples);
                this.wavSampleCount += samples.length;
            };

            this.sourceNode.connect(this.processorNode);
            this.processorNode.connect(this.audioContext.destination);

            if (this.audioContext.state === 'suspended' && this.audioContext.resume) {
                const resumePromise = this.audioContext.resume();

                if (resumePromise && resumePromise.catch) {
                    resumePromise.catch(() => {});
                }
            }

            this.scheduleWaveFlush();
        },

        updateAudioMeter(samples) {
            if (!samples || !samples.length) {
                this.audioLevel *= 0.82;
                return;
            }

            let sum = 0;

            for (let index = 0; index < samples.length; index += 1) {
                sum += samples[index] * samples[index];
            }

            const rms = Math.sqrt(sum / samples.length);
            const nextLevel = Math.min(1, Math.max(0, (rms - 0.01) * 16));
            const attack = nextLevel > this.audioLevel ? 0.65 : 0.18;

            this.audioLevel = (this.audioLevel * (1 - attack)) + (nextLevel * attack);
        },

        resetAudioMeter() {
            this.audioLevel = 0;
        },

        scheduleWaveFlush() {
            this.clearChunkTimer();

            this.chunkTimer = window.setTimeout(() => {
                this.flushWaveChunk();

                if (this.isRecording && !this.isStopping) {
                    this.scheduleWaveFlush();
                }
            }, this.chunkDuration);
        },

        clearChunkTimer() {
            if (this.chunkTimer) {
                window.clearTimeout(this.chunkTimer);
                this.chunkTimer = null;
            }
        },

        flushWaveChunk() {
            if (!this.wavSampleCount) {
                return;
            }

            const buffers = this.wavBuffers.slice();
            const sampleCount = this.wavSampleCount;
            const durationSeconds = this.wavSampleRate > 0 ? sampleCount / this.wavSampleRate : 0;

            this.wavBuffers = [];
            this.wavSampleCount = 0;
            this.sendChunk(this.createWavBlob(buffers, sampleCount, this.wavSampleRate), durationSeconds);
        },

        createWavBlob(buffers, sampleCount, sampleRate) {
            const bytesPerSample = 2;
            const channelCount = 1;
            const blockAlign = channelCount * bytesPerSample;
            const byteRate = sampleRate * blockAlign;
            const dataSize = sampleCount * bytesPerSample;
            const buffer = new ArrayBuffer(44 + dataSize);
            const view = new DataView(buffer);

            this.writeWavString(view, 0, 'RIFF');
            view.setUint32(4, 36 + dataSize, true);
            this.writeWavString(view, 8, 'WAVE');
            this.writeWavString(view, 12, 'fmt ');
            view.setUint32(16, 16, true);
            view.setUint16(20, 1, true);
            view.setUint16(22, channelCount, true);
            view.setUint32(24, sampleRate, true);
            view.setUint32(28, byteRate, true);
            view.setUint16(32, blockAlign, true);
            view.setUint16(34, bytesPerSample * 8, true);
            this.writeWavString(view, 36, 'data');
            view.setUint32(40, dataSize, true);

            let offset = 44;

            buffers.forEach((samples) => {
                for (let index = 0; index < samples.length; index += 1) {
                    const sample = Math.max(-1, Math.min(1, samples[index]));
                    view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
                    offset += bytesPerSample;
                }
            });

            return new Blob([buffer], { type: 'audio/wav' });
        },

        writeWavString(view, offset, text) {
            for (let index = 0; index < text.length; index += 1) {
                view.setUint8(offset + index, text.charCodeAt(index));
            }
        },

        async sendChunk(blob, durationSeconds) {
            if (!blob || blob.size === 0 || !this.sessionId) {
                return;
            }

            const currentIndex = this.chunkIndex;
            this.chunkIndex += 1;
            this.pendingUploads += 1;

            const roundedDurationSeconds = Math.max(1, Math.ceil(durationSeconds || (this.chunkDuration / 1000)));

            try {
                if (this.directUpload.enabled) {
                    try {
                        await this.sendChunkByDirectUpload(blob, currentIndex, roundedDurationSeconds);
                    } catch (directUploadError) {
                        await this.sendChunkByMultipart(blob, currentIndex, roundedDurationSeconds);
                    }
                } else {
                    await this.sendChunkByMultipart(blob, currentIndex, roundedDurationSeconds);
                }

                await this.refreshStatus();
            } catch (error) {
                this.status = 'error';
                this.errorMessage = this.extractErrorMessage(error);
                this.$emit('recording-error', {
                    message: this.errorMessage,
                    error,
                });
            } finally {
                this.pendingUploads = Math.max(0, this.pendingUploads - 1);
            }
        },

        async sendChunkByMultipart(blob, chunkIndex, durationSeconds) {
            const form = new FormData();

            form.append('session_id', this.sessionId);
            form.append('chunk_index', chunkIndex);
            form.append('duration_seconds', durationSeconds);
            form.append('audio', blob, `chunk-${String(chunkIndex).padStart(5, '0')}.${this.extensionForBlob(blob)}`);

            await this.httpClient().post(
                this.apiUrl('/api/audio-transcriptions/chunk'),
                form,
                this.authorizedAxiosConfig({ 'Content-Type': 'multipart/form-data' })
            );
        },

        async sendChunkByDirectUpload(blob, chunkIndex, durationSeconds) {
            const mimeType = this.normalizedBlobMimeType(blob);
            const { data } = await this.httpClient().post(
                this.apiUrl('/api/audio-transcriptions/chunk-upload-url'),
                {
                    session_id: this.sessionId,
                    chunk_index: chunkIndex,
                    duration_seconds: durationSeconds,
                    mime_type: mimeType,
                    size_bytes: blob.size,
                },
                this.authorizedAxiosConfig()
            );

            await this.uploadBlobWithSignedUrl(data.upload_url, blob, data.headers || {}, mimeType);

            await this.httpClient().post(
                this.apiUrl('/api/audio-transcriptions/chunk-uploaded'),
                {
                    session_id: this.sessionId,
                    chunk_id: data.chunk_id,
                    duration_seconds: durationSeconds,
                    size_bytes: blob.size,
                },
                this.authorizedAxiosConfig()
            );
        },

        uploadBlobWithSignedUrl(uploadUrl, blob, headers, mimeType) {
            return new Promise((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                const normalizedHeaders = this.normalizedUploadHeaders(headers, mimeType);

                xhr.open('PUT', uploadUrl, true);

                Object.keys(normalizedHeaders).forEach((headerName) => {
                    xhr.setRequestHeader(headerName, normalizedHeaders[headerName]);
                });

                xhr.onload = () => {
                    if (xhr.status >= 200 && xhr.status < 300) {
                        resolve();
                        return;
                    }

                    reject(new Error(`Falha ao enviar audio para o storage (${xhr.status}).`));
                };

                xhr.onerror = () => reject(new Error('Falha de rede ao enviar audio para o storage.'));
                xhr.ontimeout = () => reject(new Error('Tempo limite ao enviar audio para o storage.'));
                xhr.timeout = 120000;
                xhr.send(blob);
            });
        },

        normalizedUploadHeaders(headers, mimeType) {
            const normalized = {};
            const sourceHeaders = headers || {};

            Object.keys(sourceHeaders).forEach((headerName) => {
                const lowerName = headerName.toLowerCase();

                if (lowerName === 'host' || lowerName === 'content-length') {
                    return;
                }

                let value = sourceHeaders[headerName];

                if (Array.isArray(value)) {
                    value = value.join(', ');
                }

                if (value !== null && typeof value !== 'undefined') {
                    normalized[headerName] = String(value);
                }
            });

            if (!this.hasHeader(normalized, 'content-type')) {
                normalized['Content-Type'] = mimeType;
            }

            return normalized;
        },

        hasHeader(headers, headerName) {
            const normalizedHeaderName = String(headerName).toLowerCase();

            return Object.keys(headers || {}).some((name) => {
                return name.toLowerCase() === normalizedHeaderName;
            });
        },

        normalizedBlobMimeType(blob) {
            const mimeType = (blob.type || 'audio/wav').split(';')[0].toLowerCase();

            return mimeType || 'audio/wav';
        },

        normalizeDirectUploadConfig(config) {
            return {
                enabled: !!(config && config.enabled),
                disk: config && config.disk ? config.disk : null,
                expiresInSeconds: config && config.expires_in_seconds ? Number(config.expires_in_seconds) : 0,
            };
        },

        async generateSoapNote() {
            if (!this.canGenerateSoap) {
                return;
            }

            this.soapErrorMessage = '';
            this.patientHistoryErrorMessage = '';

            let patientHistory = null;

            try {
                patientHistory = this.parsePatientHistoryJson();
            } catch (error) {
                this.patientHistoryErrorMessage = error.message;
                return;
            }

            this.soapProcessingStartedAt = Date.now();
            this.soapProcessingSeconds = null;
            this.soapProcessingFailed = false;
            this.startProcessingTimer();

            try {
                const { data } = await this.httpClient().post(
                    this.apiUrl(`/api/audio-transcriptions/${this.sessionId}/soap`),
                    {
                        additional_prompt: this.soapPrompt,
                        patient_history_json: patientHistory,
                    },
                    this.authorizedAxiosConfig()
                );

                this.applyStatusPayload(data);
                this.startStatusUpdates();
                this.$emit('soap-requested', data);
            } catch (error) {
                this.soapStatus = 'error';
                this.soapErrorMessage = this.extractErrorMessage(error);
                this.soapProcessingStartedAt = null;
                this.soapProcessingFailed = true;
                this.$emit('soap-error', {
                    message: this.soapErrorMessage,
                    error,
                });
                this.stopProcessingTimerIfIdle();
            }
        },

        async askChatQuestion() {
            if (!this.canAskChat) {
                return;
            }

            this.patientHistoryErrorMessage = '';
            this.chatErrorMessage = '';

            const question = this.chatQuestion.trim();
            let patientHistory = null;

            try {
                patientHistory = this.parsePatientHistoryJson();
            } catch (error) {
                this.patientHistoryErrorMessage = error.message;
                return;
            }

            this.chatMessages.push(this.createChatMessage('user', question));
            this.chatQuestion = '';
            this.isChatBusy = true;

            try {
                const { data } = await this.httpClient().post(
                    this.apiUrl(`/api/audio-transcriptions/${this.sessionId}/chat`),
                    {
                        question,
                        patient_history_json: patientHistory,
                    },
                    this.authorizedAxiosConfig()
                );

                this.chatMessages.push(this.createChatMessage('assistant', data.answer || 'Sem resposta.'));
                this.$emit('chat-answered', data);

                if (data.patient_history_json && !this.patientHistoryText.trim()) {
                    this.patientHistoryText = JSON.stringify(data.patient_history_json, null, 2);
                }
            } catch (error) {
                this.chatErrorMessage = this.extractErrorMessage(error);
                this.$emit('chat-error', {
                    message: this.chatErrorMessage,
                    error,
                });
            } finally {
                this.isChatBusy = false;
            }
        },

        parsePatientHistoryJson() {
            const text = this.patientHistoryText.trim();

            if (!text) {
                return null;
            }

            try {
                const parsed = JSON.parse(text);

                if (!parsed || typeof parsed !== 'object') {
                    throw new Error();
                }

                return parsed;
            } catch (error) {
                throw new Error('Historico previo deve ser um JSON valido, usando objeto ou lista.');
            }
        },

        createChatMessage(role, text) {
            this.chatMessageIndex += 1;

            return {
                id: this.chatMessageIndex,
                role,
                text,
            };
        },

        async refreshStatus() {
            if (!this.sessionId) {
                return;
            }

            const { data } = await this.httpClient().get(
                this.apiUrl(`/api/audio-transcriptions/${this.sessionId}`),
                this.authorizedAxiosConfig()
            );
            this.applyStatusPayload(data);
        },

        resolveInitialRecordingToken() {
            if (this.initialRecordingToken) {
                return String(this.initialRecordingToken);
            }

            if (typeof window === 'undefined') {
                return '';
            }

            const search = window.location && window.location.search ? window.location.search : '';

            if (!search || !window.URLSearchParams) {
                return '';
            }

            return new window.URLSearchParams(search).get('recording_token') || '';
        },

        applyInitialClinicalContext() {
            if (this.initialSoapPrompt) {
                this.soapPrompt = String(this.initialSoapPrompt);
            }

            if (this.initialPatientHistoryJson === null || typeof this.initialPatientHistoryJson === 'undefined') {
                return;
            }

            if (typeof this.initialPatientHistoryJson === 'string') {
                this.patientHistoryText = this.initialPatientHistoryJson;

                return;
            }

            this.patientHistoryText = JSON.stringify(this.initialPatientHistoryJson, null, 2);
        },

        httpClient() {
            if (this.axiosClient) {
                return this.axiosClient;
            }

            if (typeof window !== 'undefined' && window.axios) {
                return window.axios;
            }

            return defaultAxios;
        },

        apiUrl(path) {
            const normalizedPath = String(path || '');
            const baseUrl = this.normalizedApiBaseUrl();

            if (!baseUrl || /^https?:\/\//i.test(normalizedPath)) {
                return normalizedPath;
            }

            return `${baseUrl}/${normalizedPath.replace(/^\/+/, '')}`;
        },

        normalizedApiBaseUrl() {
            return String(this.apiBaseUrl || '').replace(/\/+$/, '');
        },

        authorizedAxiosConfig(extraHeaders) {
            const headers = this.authorizedHeaders(extraHeaders);
            const config = Object.keys(headers).length ? { headers } : {};

            if (this.withCredentials) {
                config.withCredentials = true;
            }

            return config;
        },

        authorizedHeaders(extraHeaders) {
            const headers = Object.assign({}, this.requestHeaders || {}, extraHeaders || {});

            if (this.sessionClientToken) {
                headers['X-Klingo-Session-Token'] = this.sessionClientToken;
            }

            return headers;
        },

        applyStatusPayload(payload) {
            this.status = payload.status || this.status;
            this.partialText = payload.partial_text || '';
            this.finalText = payload.final_text || '';
            this.chunksDone = payload.chunks_done || 0;
            this.chunksTotal = payload.chunks_total || 0;
            this.soapStatus = payload.soap_status || 'idle';
            this.soapNote = payload.soap_note || '';
            this.soapJson = payload.soap_json || null;
            this.soapPrompt = payload.soap_prompt || this.soapPrompt || '';
            if (payload.patient_history_json && !this.patientHistoryText.trim()) {
                this.patientHistoryText = JSON.stringify(payload.patient_history_json, null, 2);
            }
            this.soapErrorMessage = payload.soap_error_message || '';
            this.costs = payload.costs || this.costs || null;
            this.timings = payload.timings || this.timings || null;

            this.captureProcessingTimes();
            this.$emit('status-updated', payload);
            this.emitCompletedEvent(payload);

            if (this.shouldAutoGenerateSoap()) {
                this.autoSoapRequested = true;
                this.generateSoapNote();
            }

            if (this.pusherChannel) {
                this.stopPolling();

                return;
            }

            if ((this.status === 'done' || this.status === 'error') && !this.isSoapBusy) {
                this.stopPolling();
            }
        },

        shouldAutoGenerateSoap() {
            return this.autoGenerateSoap
                && !this.autoSoapRequested
                && this.canGenerateSoap
                && this.soapStatus !== 'done'
                && !this.soapJson
                && !this.soapNote;
        },

        emitCompletedEvent(payload) {
            if (this.status !== 'done' || this.soapStatus !== 'done') {
                return;
            }

            const key = `${this.sessionId}:${this.status}:${this.soapStatus}`;

            if (this.completedEventKey === key) {
                return;
            }

            this.completedEventKey = key;
            this.$emit('completed', payload);
        },

        captureProcessingTimes() {
            if (
                this.transcriptionProcessingStartedAt
                && this.transcriptionProcessingSeconds === null
                && this.isTranscriptionReadyForDisplay()
            ) {
                this.transcriptionProcessingSeconds = this.elapsedProcessingSeconds(
                    this.transcriptionProcessingStartedAt
                );
                this.transcriptionProcessingStartedAt = null;
                this.stopProcessingTimerIfIdle();
            }

            if (this.transcriptionProcessingStartedAt && this.status === 'error') {
                this.transcriptionProcessingStartedAt = null;
                this.transcriptionProcessingFailed = true;
                this.stopProcessingTimerIfIdle();
            }

            if (
                this.soapProcessingStartedAt
                && this.soapProcessingSeconds === null
                && this.soapStatus === 'done'
                && (this.soapJson || this.soapNote)
            ) {
                this.soapProcessingSeconds = this.elapsedProcessingSeconds(this.soapProcessingStartedAt);
                this.soapProcessingStartedAt = null;
                this.stopProcessingTimerIfIdle();
            }

            if (this.soapProcessingStartedAt && this.soapStatus === 'error') {
                this.soapProcessingStartedAt = null;
                this.soapProcessingFailed = true;
                this.stopProcessingTimerIfIdle();
            }
        },

        isTranscriptionReadyForDisplay() {
            const hasDisplayedText = this.transcriptText.trim().length > 0;
            const allChunksTranscribed = this.chunksTotal > 0 && this.chunksDone >= this.chunksTotal;

            return hasDisplayedText && (this.status === 'done' || allChunksTranscribed);
        },

        elapsedProcessingSeconds(startedAt) {
            return Math.max(0, (Date.now() - startedAt) / 1000);
        },

        processingLabel(seconds, startedAt, emptyLabel) {
            if (seconds !== null) {
                return this.formatDuration(seconds);
            }

            if (startedAt) {
                return `Processando ${this.formatDuration((this.processingNow - startedAt) / 1000)}`;
            }

            return emptyLabel;
        },

        formatDuration(seconds) {
            const roundedSeconds = Math.max(0, Math.round(seconds));
            const minutes = Math.floor(roundedSeconds / 60);
            const remainingSeconds = roundedSeconds % 60;

            if (minutes > 0) {
                return `${minutes}min ${String(remainingSeconds).padStart(2, '0')}s`;
            }

            return `${remainingSeconds}s`;
        },

        hasTimingValue(value) {
            return value !== null && value !== undefined && !Number.isNaN(Number(value));
        },

        costNumber(path) {
            const value = this.costValue(path);

            return Number(value || 0);
        },

        costString(path) {
            const value = this.costValue(path);

            return value ? String(value) : 'modelo nao registrado';
        },

        costValue(path) {
            if (!this.costs) {
                return null;
            }

            return path.split('.').reduce((current, key) => {
                if (!current || typeof current !== 'object') {
                    return null;
                }

                return current[key];
            }, this.costs);
        },

        formatCost(value) {
            const amount = Number(value || 0);
            const currency = this.costValue('currency') ? String(this.costValue('currency')) : 'USD';

            return `${currency} ${amount.toFixed(6)}`;
        },

        humanizeKey(key) {
            return String(key).replace(/_/g, ' ');
        },

        formatAnalysisValue(value) {
            if (Array.isArray(value)) {
                return value.length ? value.join('; ') : 'Nao informado';
            }

            if (typeof value === 'boolean') {
                return value ? 'sim' : 'nao';
            }

            return value || 'Nao informado';
        },

        startProcessingTimer() {
            this.processingNow = Date.now();

            if (this.processingTimer) {
                return;
            }

            this.processingTimer = window.setInterval(() => {
                this.processingNow = Date.now();
            }, 1000);
        },

        stopProcessingTimerIfIdle() {
            if (this.transcriptionProcessingStartedAt || this.soapProcessingStartedAt) {
                return;
            }

            if (this.processingTimer) {
                window.clearInterval(this.processingTimer);
                this.processingTimer = null;
            }
        },

        async stopRecorder() {
            this.clearChunkTimer();
            this.flushWaveChunk();
            await this.stopAudioGraph();
        },

        waitForPendingUploads() {
            return new Promise((resolve, reject) => {
                let attempts = 0;
                const timer = window.setInterval(() => {
                    attempts += 1;

                    if (this.pendingUploads === 0) {
                        window.clearInterval(timer);
                        resolve();
                    }

                    if (attempts > 80) {
                        window.clearInterval(timer);
                        reject(new Error('Tempo limite ao enviar os ultimos chunks.'));
                    }
                }, 250);
            });
        },

        resolveAudioContextConstructor() {
            return window.AudioContext || window.webkitAudioContext || null;
        },

        extensionForBlob(blob) {
            const mimeType = (blob.type || '').split(';')[0];

            if (mimeType.includes('wav')) {
                return 'wav';
            }

            if (mimeType.includes('mp4')) {
                return 'mp4';
            }

            if (mimeType.includes('mpeg')) {
                return 'mp3';
            }

            return 'webm';
        },

        startPolling() {
            this.stopPolling();

            this.pollingTimer = window.setInterval(() => {
                this.refreshStatus().catch((error) => {
                    this.errorMessage = this.extractErrorMessage(error);
                });
            }, 2500);
        },

        stopPolling() {
            if (this.pollingTimer) {
                window.clearInterval(this.pollingTimer);
                this.pollingTimer = null;
            }
        },

        startStatusUpdates() {
            if (this.pusherChannel) {
                return;
            }

            if (this.subscribeToRealtimeSession()) {
                return;
            }

            this.startPolling();
        },

        subscribeToRealtimeSession() {
            this.unsubscribeFromRealtimeSession();

            const config = this.resolvedPusherConfig();

            if (!this.sessionId || !config.enabled || !config.key || typeof window === 'undefined' || !window.Pusher) {
                return false;
            }

            const options = {
                cluster: config.cluster || 'mt1',
                forceTLS: config.scheme !== 'http',
                enabledTransports: ['ws', 'wss'],
            };

            if (config.host) {
                options.wsHost = config.host;
                options.httpHost = config.host;
                options.wsPort = Number(config.port || 80);
                options.wssPort = Number(config.port || 443);
            }

            this.pusherClient = new window.Pusher(config.key, options);
            this.pusherChannelName = `${config.channelPrefix || 'audio-transcription'}-${this.sessionId}`;
            this.pusherChannel = this.pusherClient.subscribe(this.pusherChannelName);
            this.pusherChannel.bind(config.eventName || 'audio-transcription-updated', this.handleRealtimeNotification);
            this.pusherChannel.bind('pusher:subscription_error', () => {
                this.unsubscribeFromRealtimeSession();
                this.startPolling();
            });

            return true;
        },

        resolvedPusherConfig() {
            if (this.pusherConfig) {
                return this.pusherConfig;
            }

            if (typeof window !== 'undefined' && window.KLINGO_PUSHER_CONFIG) {
                return window.KLINGO_PUSHER_CONFIG;
            }

            return {};
        },

        unsubscribeFromRealtimeSession() {
            if (this.realtimeRefreshTimer) {
                window.clearTimeout(this.realtimeRefreshTimer);
                this.realtimeRefreshTimer = null;
            }

            if (this.pusherChannel) {
                this.pusherChannel.unbind();
                this.pusherChannel = null;
            }

            if (this.pusherClient && this.pusherChannelName) {
                this.pusherClient.unsubscribe(this.pusherChannelName);
            }

            if (this.pusherClient) {
                this.pusherClient.disconnect();
                this.pusherClient = null;
            }

            this.pusherChannelName = null;
        },

        handleRealtimeNotification(payload) {
            const notification = this.normalizeRealtimePayload(payload);

            if (!notification || notification.session_id !== this.sessionId) {
                return;
            }

            this.scheduleRealtimeRefresh();
        },

        normalizeRealtimePayload(payload) {
            if (typeof payload === 'string') {
                try {
                    return JSON.parse(payload);
                } catch (error) {
                    return null;
                }
            }

            return payload || null;
        },

        scheduleRealtimeRefresh() {
            if (this.realtimeRefreshTimer) {
                return;
            }

            this.realtimeRefreshTimer = window.setTimeout(() => {
                this.realtimeRefreshTimer = null;
                this.refreshStatus().catch((error) => {
                    this.errorMessage = this.extractErrorMessage(error);
                });
            }, 250);
        },

        startElapsedTimer() {
            this.stopElapsedTimer();

            this.elapsedTimer = window.setInterval(() => {
                this.elapsedSeconds += 1;
            }, 1000);
        },

        stopElapsedTimer() {
            if (this.elapsedTimer) {
                window.clearInterval(this.elapsedTimer);
                this.elapsedTimer = null;
            }
        },

        stopStream() {
            if (this.stream) {
                this.stream.getTracks().forEach((track) => track.stop());
                this.stream = null;
            }
        },

        async stopAudioGraph() {
            if (this.processorNode) {
                this.processorNode.onaudioprocess = null;
                this.processorNode.disconnect();
                this.processorNode = null;
            }

            if (this.sourceNode) {
                this.sourceNode.disconnect();
                this.sourceNode = null;
            }

            if (this.audioContext) {
                const context = this.audioContext;
                this.audioContext = null;

                if (context.state !== 'closed' && context.close) {
                    try {
                        await context.close();
                    } catch (error) {
                        // The stream tracks are stopped separately, so a close failure is non-fatal.
                    }
                }
            }

            this.wavBuffers = [];
            this.wavSampleCount = 0;
            this.resetAudioMeter();
        },

        cleanup() {
            this.stopPolling();
            this.unsubscribeFromRealtimeSession();
            this.stopElapsedTimer();
            this.transcriptionProcessingStartedAt = null;
            this.soapProcessingStartedAt = null;
            this.stopProcessingTimerIfIdle();
            this.clearChunkTimer();
            this.stopAudioGraph();
            this.stopStream();
        },

        extractErrorMessage(error) {
            var response = error && error.response ? error.response : null;
            var data = response && response.data ? response.data : {};
            var errors = data.errors || {};

            return data.message
                || (errors.audio && errors.audio[0])
                || (errors.session_id && errors.session_id[0])
                || (errors.chunk_id && errors.chunk_id[0])
                || (errors.mime_type && errors.mime_type[0])
                || (errors.size_bytes && errors.size_bytes[0])
                || (errors.direct_upload && errors.direct_upload[0])
                || (errors.additional_prompt && errors.additional_prompt[0])
                || (errors.patient_history_json && errors.patient_history_json[0])
                || (errors.question && errors.question[0])
                || (error && error.message)
                || 'Erro inesperado.';
        },
    },
};
</script>

<style scoped>
.recorder-shell {
    min-height: 100vh;
    display: flex;
    align-items: stretch;
    background: #f8f9fa;
    color: #212529;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif;
}

.recorder-panel {
    width: min(1280px, calc(100% - 32px));
    margin: 0 auto;
    padding: 32px 0;
}

.recorder-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 24px;
}

.eyebrow {
    margin: 0 0 6px;
    color: #6c757d;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0;
    text-transform: uppercase;
}

h1,
h2 {
    margin: 0;
    letter-spacing: 0;
}

h1 {
    font-size: 32px;
    line-height: 1.1;
}

h2 {
    font-size: 17px;
}

.status-pill {
    min-height: 38px;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 0 12px;
    background: #ffffff;
    color: #212529;
    font-size: 14px;
    font-weight: 700;
    white-space: nowrap;
}

.status-dot {
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #6c757d;
}

.status-pill.is-recording .status-dot {
    background: #dc3545;
}

.status-pill.is-processing .status-dot {
    background: #ffc107;
}

.status-pill.is-done .status-dot {
    background: #28a745;
}

.status-pill.is-error .status-dot {
    background: #dc3545;
}

.recorder-grid {
    display: grid;
    grid-template-columns: 360px minmax(0, 1fr);
    gap: 18px;
    align-items: stretch;
}

.control-surface,
.transcript-surface,
.history-surface,
.chat-surface,
.soap-surface {
    background: #ffffff;
    border: 1px solid #dee2e6;
    border-radius: 4px;
}

.control-surface {
    min-height: 520px;
    display: flex;
    flex-direction: column;
    gap: 22px;
    padding: 24px;
}

.input-select-field {
    display: grid;
    gap: 8px;
}

.input-select-field span {
    color: #6c757d;
    font-size: 13px;
    font-weight: 800;
}

.input-select-field select {
    width: 100%;
    min-height: 44px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    padding: 0 12px;
    color: #495057;
    background: #ffffff;
    font-size: 14px;
    font-weight: 700;
}

.input-select-field select:focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.input-select-field select:disabled {
    background: #e9ecef;
    cursor: not-allowed;
    opacity: 0.72;
}

.record-button,
.stop-button {
    width: 100%;
    min-height: 112px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    border: 0;
    border-radius: 4px;
    color: #ffffff;
    cursor: pointer;
    font-size: 24px;
    font-weight: 800;
}

.record-button {
    background: #28a745;
}

.stop-button {
    background: #dc3545;
}

.record-button:hover:not(:disabled) {
    background: #218838;
}

.stop-button:hover:not(:disabled) {
    background: #c82333;
}

.record-button:focus,
.stop-button:focus,
.soap-button:focus,
.chat-controls button:focus {
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.record-button:disabled,
.stop-button:disabled {
    cursor: not-allowed;
    opacity: 0.65;
}

.record-icon {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: currentColor;
}

.stop-icon {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    background: currentColor;
}

.audio-meter {
    display: grid;
    gap: 10px;
    min-height: 86px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 12px;
    background: #f8f9fa;
}

.audio-meter-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    color: #6c757d;
    font-size: 13px;
    font-weight: 800;
}

.audio-meter-title strong {
    color: #6c757d;
    font-size: 13px;
    font-weight: 800;
    white-space: nowrap;
}

.audio-meter.has-signal .audio-meter-title strong {
    color: #28a745;
}

.audio-meter-bars {
    height: 42px;
    display: grid;
    grid-template-columns: repeat(14, minmax(0, 1fr));
    align-items: stretch;
    gap: 5px;
}

.audio-meter-bar {
    display: block;
    min-width: 0;
    border-radius: 4px;
    background: #ced4da;
    transform-origin: center bottom;
    transition:
        transform 90ms linear,
        opacity 90ms linear,
        background 160ms ease;
}

.audio-meter.is-active .audio-meter-bar {
    background: linear-gradient(180deg, #007bff 0%, #28a745 100%);
}

.metrics {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    margin: 0;
}

.processing-metrics {
    display: grid;
    grid-template-columns: 1fr;
    gap: 10px;
    margin: 0;
    border-top: 1px solid #dee2e6;
    padding-top: 16px;
}

.metrics div,
.processing-metrics div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 48px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 0 12px;
    background: #f8f9fa;
}

.processing-metrics div {
    align-items: flex-start;
    flex-direction: column;
    justify-content: center;
    gap: 4px;
}

.metrics dt,
.processing-metrics dt {
    color: #6c757d;
    font-size: 13px;
    font-weight: 700;
}

.metrics dd,
.processing-metrics dd {
    margin: 0;
    font-size: 17px;
    font-weight: 800;
}

.processing-metrics dd {
    color: #007bff;
}

.processing-metrics dd strong {
    display: block;
    font-size: 17px;
}

.processing-metrics dd small {
    display: block;
    margin-top: 2px;
    color: #6c757d;
    font-size: 12px;
    font-weight: 700;
    line-height: 1.35;
}

.session-box {
    margin-top: auto;
    display: grid;
    gap: 8px;
    border-top: 1px solid #dee2e6;
    padding-top: 18px;
    color: #6c757d;
    font-size: 13px;
    font-weight: 700;
}

.session-box code {
    display: block;
    width: 100%;
    overflow-wrap: anywhere;
    color: #212529;
    font-size: 12px;
    font-weight: 600;
}

.document-column {
    display: grid;
    grid-template-rows: minmax(360px, 1fr) auto minmax(420px, auto);
    gap: 18px;
    min-width: 0;
}

.transcript-surface,
.history-surface,
.soap-surface {
    min-height: 520px;
    display: flex;
    flex-direction: column;
}

.history-surface {
    min-height: 280px;
}

.soap-surface {
    min-height: 420px;
}

.transcript-toolbar {
    min-height: 62px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border-bottom: 1px solid #dee2e6;
    padding: 0 18px;
}

.transcript-toolbar span {
    color: #6c757d;
    font-size: 13px;
    font-weight: 700;
}

.transcript-area {
    flex: 1;
    width: 100%;
    min-height: 320px;
    border: 0;
    resize: none;
    padding: 18px;
    color: #212529;
    background: #ffffff;
    font-size: 16px;
    line-height: 1.65;
}

.transcript-area:focus {
    outline: 2px solid #80bdff;
    outline-offset: -2px;
}

.history-area,
.history-analysis-area {
    width: 100%;
    min-height: 180px;
    border: 0;
    resize: vertical;
    padding: 18px;
    color: #212529;
    background: #ffffff;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
    font-size: 13px;
    line-height: 1.55;
}

.history-analysis-area {
    min-height: 150px;
    font-family: inherit;
    font-size: 14px;
}

.history-area:focus,
.history-analysis-area:focus {
    outline: 2px solid #80bdff;
    outline-offset: -2px;
}

.soap-controls {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 150px;
    gap: 10px 12px;
    border-bottom: 1px solid #dee2e6;
    padding: 14px 18px;
}

.soap-controls label {
    grid-column: 1 / -1;
    color: #6c757d;
    font-size: 13px;
    font-weight: 800;
}

.prompt-area {
    width: 100%;
    min-height: 72px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    resize: vertical;
    padding: 10px 12px;
    color: #495057;
    background: #ffffff;
    font-size: 14px;
    line-height: 1.45;
}

.prompt-area:focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.prompt-area:disabled {
    background: #e9ecef;
    cursor: not-allowed;
}

.soap-button {
    align-self: stretch;
    min-height: 72px;
    border: 0;
    border-radius: 4px;
    background: #007bff;
    color: #ffffff;
    cursor: pointer;
    font-size: 15px;
    font-weight: 800;
}

.soap-button:hover:not(:disabled),
.chat-controls button:hover:not(:disabled) {
    background: #0069d9;
}

.soap-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

.soap-area {
    flex: 1;
    width: 100%;
    min-height: 240px;
    border: 0;
    resize: none;
    padding: 18px;
    color: #212529;
    background: #ffffff;
    font-size: 15px;
    line-height: 1.6;
}

.soap-area:focus {
    outline: 2px solid #80bdff;
    outline-offset: -2px;
}

.json-toolbar {
    min-height: 46px;
    display: flex;
    align-items: center;
    border-top: 1px solid #dee2e6;
    border-bottom: 1px solid #dee2e6;
    padding: 0 18px;
    background: #f8f9fa;
}

.json-toolbar h3 {
    margin: 0;
    color: #212529;
    font-size: 14px;
    font-weight: 800;
    letter-spacing: 0;
}

.json-area {
    width: 100%;
    min-height: 260px;
    border: 0;
    resize: vertical;
    padding: 18px;
    color: #212529;
    background: #ffffff;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace;
    font-size: 13px;
    line-height: 1.55;
}

.json-area:focus {
    outline: 2px solid #80bdff;
    outline-offset: -2px;
}

.chat-surface {
    margin-top: 18px;
}

.chat-surface .cost-summary-header span {
    color: #6c757d;
    font-size: 13px;
    font-weight: 700;
}

.chat-messages {
    display: grid;
    gap: 12px;
    min-height: 120px;
    padding: 18px;
    background: #f8f9fa;
}

.chat-message {
    max-width: min(760px, 92%);
    display: grid;
    gap: 6px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 12px 14px;
    background: #ffffff;
}

.chat-message.is-user {
    justify-self: end;
    border-color: #b8daff;
    background: #e7f3ff;
}

.chat-message strong {
    color: #6c757d;
    font-size: 12px;
    text-transform: uppercase;
}

.chat-message p,
.chat-empty {
    margin: 0;
    color: #212529;
    font-size: 14px;
    line-height: 1.55;
    white-space: pre-wrap;
}

.chat-empty {
    color: #6c757d;
    font-weight: 700;
}

.chat-controls {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 150px;
    gap: 12px;
    border-top: 1px solid #dee2e6;
    padding: 14px 18px;
}

.chat-controls textarea {
    width: 100%;
    min-height: 78px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    resize: vertical;
    padding: 10px 12px;
    color: #495057;
    background: #ffffff;
    font-size: 14px;
    line-height: 1.45;
}

.chat-controls textarea:focus {
    border-color: #80bdff;
    outline: 0;
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.chat-controls textarea:disabled {
    background: #e9ecef;
    cursor: not-allowed;
}

.chat-controls button {
    min-height: 78px;
    border: 0;
    border-radius: 4px;
    background: #007bff;
    color: #ffffff;
    cursor: pointer;
    font-size: 15px;
    font-weight: 800;
}

.chat-controls button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
}

.cost-summary-surface {
    margin-top: 18px;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    background: #ffffff;
}

.cost-summary-header {
    min-height: 62px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    border-bottom: 1px solid #dee2e6;
    padding: 0 18px;
}

.cost-summary-header strong {
    color: #007bff;
    font-size: 19px;
}

.cost-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0;
}

.cost-item {
    display: grid;
    gap: 6px;
    min-height: 112px;
    padding: 18px;
}

.cost-item + .cost-item {
    border-left: 1px solid #dee2e6;
}

.cost-item span {
    color: #6c757d;
    font-size: 13px;
    font-weight: 800;
}

.cost-item strong {
    color: #212529;
    font-size: 24px;
    line-height: 1.1;
}

.cost-item small {
    color: #6c757d;
    font-size: 13px;
    font-weight: 700;
    line-height: 1.4;
}

.error-message {
    margin: 0;
    border-top: 1px solid #f5c6cb;
    padding: 14px 18px;
    color: #721c24;
    background: #f8d7da;
    font-size: 14px;
    font-weight: 700;
}

@media (max-width: 860px) {
    .recorder-panel {
        width: min(100% - 20px, 680px);
        padding: 18px 0;
    }

    .recorder-header,
    .transcript-toolbar {
        align-items: flex-start;
        flex-direction: column;
    }

    h1 {
        font-size: 26px;
    }

    .recorder-grid {
        grid-template-columns: 1fr;
    }

    .control-surface,
    .transcript-surface,
    .history-surface,
    .soap-surface {
        min-height: auto;
    }

    .document-column {
        grid-template-rows: auto;
    }

    .soap-controls,
    .chat-controls {
        grid-template-columns: 1fr;
    }

    .cost-summary-header,
    .cost-grid {
        grid-template-columns: 1fr;
    }

    .cost-summary-header {
        align-items: flex-start;
        flex-direction: column;
        justify-content: center;
        padding: 14px 18px;
    }

    .cost-item + .cost-item {
        border-top: 1px solid #e2e8eb;
        border-left: 0;
    }

    .record-button,
    .stop-button {
        min-height: 84px;
        font-size: 21px;
    }

    .audio-meter {
        min-height: 78px;
    }

    .audio-meter-bars {
        height: 36px;
        gap: 4px;
    }

    .transcript-area {
        min-height: 360px;
    }

    .soap-area {
        min-height: 300px;
    }

    .json-area {
        min-height: 280px;
    }
}
</style>
